import httpClient from './http-client'

export type UserSubscriptionStatus = 'trialing' | 'paid' | 'cancelling' | 'cancelled' | null

export type CustomerEventType = 'trial_end' | 'renewal' | 'access_ends'

export interface CustomerNextEvent {
  type: CustomerEventType
  at: string
  days_until: number
}

export interface LightbaseUser {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  created_at: string | null
  last_active_at: string | null
  days_inactive: number | null
  organization_id: string | null
  organization_name: string | null
  subscription_status: UserSubscriptionStatus
  trial_end: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  canceled_at: string | null
  cancellation_reason: string | null
  cancellation_feedback: string | null
  next_event: CustomerNextEvent | null
  at_risk: boolean
}

export interface CustomersRetentionSummary {
  trials_ending_7d: number
  renewals_7d: number
  access_ending_7d: number
  at_risk: number
  cancelling: number
  at_risk_inactive_days: number
  upcoming_event_days: number
}

export interface UsersListResponse {
  activity: {
    today: number
    last_7_days: number
    last_30_days: number
  }
  retention: CustomersRetentionSummary
  users: LightbaseUser[]
}

export type ActivityPeriod = 'last_7_days' | 'last_30_days' | 'week' | 'month'

export interface UserActivityDay {
  date: string
  active_seconds: number
  active_hours: number
  active: boolean
}

export interface UserActivityResponse {
  user_id: string
  start: string
  end: string
  period: string
  days_active: number
  days_in_range: number
  active_seconds: number
  active_hours: number
  session_count: number
  daily: UserActivityDay[]
  user: {
    id: string
    email: string | null
    first_name: string | null
    last_name: string | null
    last_active_at: string | null
  }
}

export interface CustomerNote {
  id: string
  user_id: string
  body: string
  author_user_id: string | null
  author_email: string | null
  created_at: string
}

export interface CustomerNotesResponse {
  notes: CustomerNote[]
}

export interface PitchUsageSummary {
  used: number
  base_limit: number
  bonus: number
  limit: number
  remaining: number
  /** Auto-drafted pitches for recommended brands this period; free of the limit. */
  auto_generated?: number
  period_start: string
  period_end: string
}

export interface PitchLimitGrant {
  id: string
  user_id: string
  amount: number
  reason: string
  period_start: string
  period_end: string
  author_user_id: string | null
  author_email: string | null
  created_at: string
  revoked_at: string | null
}

export interface PitchGrantsResponse {
  usage: PitchUsageSummary
  grants: PitchLimitGrant[]
}

export interface PitchGrantMutationResponse {
  grant: PitchLimitGrant
  usage: PitchUsageSummary
}

export const usersApi = {
  list: () => httpClient.get<UsersListResponse>('/lightbase/users'),
  activity: (
    userId: string,
    params: {
      period?: ActivityPeriod
      week_start?: string
      month?: string
      start?: string
      end?: string
    } = {},
  ) => {
    const query: Record<string, string> = {
      period: params.period ?? 'last_7_days',
    }
    if (params.week_start) query.week_start = params.week_start
    if (params.month) query.month = params.month
    if (params.start) query.start = params.start
    if (params.end) query.end = params.end
    return httpClient.get<UserActivityResponse>(`/lightbase/users/${userId}/activity`, {
      params: query,
    })
  },
  listNotes: (userId: string) =>
    httpClient.get<CustomerNotesResponse>(`/lightbase/users/${userId}/notes`),
  createNote: (userId: string, body: string) =>
    httpClient.post<CustomerNote>(`/lightbase/users/${userId}/notes`, { body }),
  listPitchGrants: (userId: string) =>
    httpClient.get<PitchGrantsResponse>(`/lightbase/users/${userId}/pitch-grants`),
  createPitchGrant: (userId: string, amount: number, reason: string) =>
    httpClient.post<PitchGrantMutationResponse>(`/lightbase/users/${userId}/pitch-grants`, {
      amount,
      reason,
    }),
  revokePitchGrant: (userId: string, grantId: string) =>
    httpClient.post<PitchGrantMutationResponse>(
      `/lightbase/users/${userId}/pitch-grants/${grantId}/revoke`,
    ),
}
